import React, { useState } from 'react';
import { TemplateCategory } from '../../types/survey.types';
import { stripHTML } from '../../utils/stripHTML';

interface SaveAsTemplateModalProps {
  surveyTitle: string;
  surveyDescription?: string;
  onSave: (name: string, description: string, category: TemplateCategory, tags: string[]) => Promise<void>;
  onClose: () => void;
  loading: boolean;
}

const categoryOptions: { value: TemplateCategory; label: string }[] = [
  { value: 'patient_feedback', label: 'Patient Feedback' },
  { value: 'clinical_research', label: 'Clinical Research' },
  { value: 'product_evaluation', label: 'Product Evaluation' },
  { value: 'general', label: 'General' },
  { value: 'custom', label: 'Custom' },
];

const SaveAsTemplateModal: React.FC<SaveAsTemplateModalProps> = ({
  surveyTitle,
  surveyDescription,
  onSave,
  onClose,
  loading,
}) => {
  const [name, setName] = useState(surveyTitle);
  const [description, setDescription] = useState(stripHTML(surveyDescription || ''));
  const [category, setCategory] = useState<TemplateCategory>('general');
  const [tagsInput, setTagsInput] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    const tags = tagsInput
      .split(',')
      .map(t => t.trim())
      .filter(t => t.length > 0);

    await onSave(name.trim(), description.trim(), category, tags);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-bold" style={{ color: 'var(--color-primary)', fontFamily: 'var(--font-heading)' }}>
            Save as Template
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 hover:bg-gray-100 rounded-lg"
            aria-label="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <p className="text-sm mb-4" style={{ color: 'var(--color-muted)' }}>
          Save this survey as a reusable template that you can use to quickly create new surveys.
        </p>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-primary)' }}>
              Template Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: 'var(--glass-border-light)' }}
              placeholder="Enter template name"
              required
              autoFocus
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-primary)' }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
              style={{ borderColor: 'var(--glass-border-light)' }}
              placeholder="Describe what this template is for"
              rows={2}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-primary)' }}>
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as TemplateCategory)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 bg-white"
              style={{ borderColor: 'var(--glass-border-light)' }}
            >
              {categoryOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-primary)' }}>
              Tags (comma-separated)
            </label>
            <input
              type="text"
              value={tagsInput}
              onChange={(e) => setTagsInput(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
              style={{ borderColor: 'var(--glass-border-light)' }}
              placeholder="e.g. patient, feedback, clinical"
            />
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="glass-btn-secondary glass-btn-sm"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="glass-btn-primary glass-btn-sm"
              disabled={loading || !name.trim()}
            >
              {loading ? 'Saving...' : 'Save Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveAsTemplateModal;
